import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  ATSConnectHiringRequestCandidateFeedbackTestIds,
  ATSPrincipalAppReferencesModalTestIds,
} from 'data-testids/ATS';
import { Dialog, useTheme } from '@mui/material';
import {
  CandidateFeedbackListModalPaneValue,
  CandidateFeedbackModalPane,
  ReadableFinalRecommendation,
} from 'features/Connect/features/ConnectATSIntegration/types/feedbackDetailTypes';
import {
  CandidateFeedbackListModalPane,
  candidateFeedbackToasts,
  finalRecommendationMap,
  HiringRequestToast,
} from 'features/Connect/features/ConnectATSIntegration/enums';
import { HiringRequestToastType } from 'features/Connect/features/ConnectATSIntegration/types';
import { EditPane } from 'features/Connect/features/ConnectATSIntegration/CandidateFeedback/Scorecard/Edit/EditPane';
import { PrincipalApplicationModal } from '../constants';
import { ReferenceFeedbackListPane } from 'features/Connect/features/ConnectATSIntegration/CandidateFeedback/FeedbackList/FeedbackListPane';
import { ReferencesModalProps } from 'features/Principal/types/referencesTypes';
import { useQueryGetCompletedAndDraftReferences } from 'features/References/api/referenceQueries';
import { ViewCTAButtons } from 'features/Connect/features/ConnectATSIntegration/CandidateFeedback/Scorecard/View/ViewCTAButtons';
import { FormContainer } from '../styles';
import { ReferencePane } from 'features/References/ReferencePane';
import { EditCTAButtons } from 'features/Connect/features/ConnectATSIntegration/CandidateFeedback/Scorecard/Edit/components/EditCTAButtons';
import { ScorecardToTransform } from 'features/Connect/features/ConnectATSIntegration/types/scorecardTypes';
import { FinalRecommendation } from 'utils/typedEnums';
import { useEditReference } from 'features/References/api/referenceMutations';
import { ErrorSnackbar, SuccessSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import auth from 'utils/auth';

export const ReferencesModal: React.FC<ReferencesModalProps> = ({
  candidate,
  open,
  onClose,
  roleTitle,
}) => {
  const [currentReferencePane, setCurrentReferencePane] = useState<CandidateFeedbackModalPane>({
    type: CandidateFeedbackListModalPane.REFERENCE_LIST,
    id: null,
  });
  const [currentToast, setCurrentToast] = useState<{
    type: HiringRequestToastType;
    message: string;
  }>({ type: null, message: null });
  const { data: completedReferences } = useQueryGetCompletedAndDraftReferences(candidate.id);
  const completedReference = completedReferences?.find(
    (reference) => reference.id === currentReferencePane.id
  );
  const theme = useTheme();
  const districtId = auth.getUser()?.profile?.district.id;
  const {
    mutate: editReferenceCheck,
    error: editReferenceError,
    isLoading: isEditReferenceMutationLoading,
  } = useEditReference(candidate.id, districtId, completedReference?.id, {
    onSuccess: () => {
      setCurrentReferencePane({ type: CandidateFeedbackListModalPane.REFERENCE_LIST });
      setCurrentToast({
        type: HiringRequestToast.SUCCESS,
        message: candidateFeedbackToasts.referenceUpdated,
      });
    },
    onError: () => {
      setCurrentToast({
        type: HiringRequestToast.ERROR,
        message: errorMessageFromEditingReference,
      });
    },
  });

  const errorMessageFromEditingReference = getAxiosErrorMessageByStatusCode(editReferenceError);

  const editMethods = useForm<{ candidate_id: number; completed_scorecard_id?: number }>({
    defaultValues: { candidate_id: candidate.id },
  });

  const updatedOnClose = (type: CandidateFeedbackListModalPaneValue) => {
    setCurrentReferencePane({ type });
    onClose();
  };

  const handleSetCurrentModalPane = (pane: CandidateFeedbackModalPane) => {
    setCurrentReferencePane(pane);
  };

  const handleViewClick = (id: number) => {
    return handleSetCurrentModalPane({
      type: CandidateFeedbackListModalPane.REFERENCE_VIEW,
      id,
    });
  };

  const handleCancel = (type: CandidateFeedbackListModalPaneValue) =>
    setCurrentReferencePane({ type });

  const handleSubmit = (formState: ScorecardToTransform, isComplete: boolean, isEdit = false) => {
    const formData = new FormData();

    const processedAnswers = formState.answers.map((answer) => {
      if (answer.attachment instanceof File) {
        formData.append(answer.attachment.name, answer.attachment);
        return {
          ...answer,
          attachment: answer.attachment.name,
        };
      }

      return answer;
    });

    let finalRecommendationValue: FinalRecommendation | ReadableFinalRecommendation =
      formState.final_recommendation;

    if (Object.keys(finalRecommendationMap).includes(formState.final_recommendation)) {
      finalRecommendationValue = finalRecommendationMap[formState.final_recommendation];
    }

    const payload = {
      ...formState,
      answers: processedAnswers,
      final_recommendation: finalRecommendationValue,
      isComplete,
    };

    formData.append('data', JSON.stringify(payload));

    if (isEdit) {
      editReferenceCheck({
        candidateId: candidate.id,
        referenceId: payload.id,
        payload: formData,
        isComplete,
      });
    }
  };

  const handleEdit = (formState: ScorecardToTransform) => {
    const filteredFormState = Object.fromEntries(
      Object.entries(formState).filter(([key]) => key !== 'submitted_by')
    );

    handleSubmit(
      {
        ...filteredFormState,
        custom_scorecard_id: formState.custom_scorecard_id,
        answers: formState.answers,
      },
      true,
      true
    );
  };

  const handleSave = (formState: ScorecardToTransform, isComplete: boolean) => {
    if (completedReference) {
      handleSubmit(formState, isComplete, true);
    } else {
      handleSubmit(formState, isComplete, false);
    }
  };

  const handleEditClick = (id: number) => {
    const type = CandidateFeedbackListModalPane.REFERENCE_EDIT;

    return handleSetCurrentModalPane({
      type,
      id,
    });
  };

  return (
    <Dialog
      data-testid={ATSPrincipalAppReferencesModalTestIds.MODAL}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: theme.spacing(2),
        },
      }}
    >
      <FormContainer>
        {currentReferencePane.type === CandidateFeedbackListModalPane.REFERENCE_LIST && (
          <FormProvider {...editMethods}>
            <ReferenceFeedbackListPane
              onClose={() => updatedOnClose(CandidateFeedbackListModalPane.REFERENCE_LIST)}
              roleTitle={roleTitle}
              titleText={`References for ${candidate.first_name} ${candidate.last_name}`}
              onModalPaneChange={handleSetCurrentModalPane}
              modalType={PrincipalApplicationModal.REFERENCE}
              onEditClick={handleEditClick}
              onViewClick={handleViewClick}
              candidateId={candidate.id}
            />
          </FormProvider>
        )}
        {currentReferencePane.type === CandidateFeedbackListModalPane.REFERENCE_VIEW && (
          <FormProvider {...editMethods}>
            <EditPane
              candidateName={`${candidate.first_name} ${candidate.last_name}`}
              created={completedReference.created}
              modalType={PrincipalApplicationModal.REFERENCE}
              reviewer={completedReference.submitted_by_name}
              templateName={completedReference.template_name}
              isReadOnly={true}
            />
            <ViewCTAButtons
              onEditClick={() => handleEditClick(currentReferencePane.id)}
              onCancel={() => handleCancel(CandidateFeedbackListModalPane.REFERENCE_LIST)}
              authorEmail={completedReference.submitted_by}
            />
          </FormProvider>
        )}
        {currentReferencePane.type === CandidateFeedbackListModalPane.REFERENCE_CREATE && (
          <ReferencePane
            onCancel={() => handleCancel(CandidateFeedbackListModalPane.REFERENCE_LIST)}
            referenceId={completedReference?.id}
            roleTitle={roleTitle}
            hiringRequestId={null}
            setFeedbackModalToast={setCurrentToast}
            candidate={candidate}
          />
        )}
        {currentReferencePane.type === CandidateFeedbackListModalPane.REFERENCE_EDIT && (
          <FormProvider {...editMethods}>
            <EditPane
              candidateName={`${candidate.first_name} ${candidate.last_name}`}
              created={completedReference.created}
              modalType={PrincipalApplicationModal.REFERENCE}
              reviewer={completedReference.submitted_by_name}
              templateName={completedReference.template_name}
            />
            <EditCTAButtons
              isLoading={isEditReferenceMutationLoading}
              onSave={(formState) => handleEdit(formState)}
              onCancel={() => handleCancel(CandidateFeedbackListModalPane.REFERENCE_LIST)}
              isScorecard={false}
              isComplete={completedReference.is_complete}
              onSaveAsDraft={(formState) => {
                handleSave(formState, false);
              }}
            />
          </FormProvider>
        )}
        <ErrorSnackbar
          dataTestId={ATSConnectHiringRequestCandidateFeedbackTestIds.ERROR_TOAST} // UPDATE
          open={currentToast?.type === HiringRequestToast.ERROR}
          message={errorMessageFromEditingReference}
          onClose={() => setCurrentToast({ type: null, message: null })}
        />
        <SuccessSnackbar
          dataTestId={ATSConnectHiringRequestCandidateFeedbackTestIds.SUCCESS_TOAST}
          open={currentToast?.type === HiringRequestToast.SUCCESS}
          message={currentToast?.message}
          onClose={() => setCurrentToast(null)}
        />
      </FormContainer>
    </Dialog>
  );
};
